/**
 * Арифметика границы историчного назначения — одна на все контуры.
 *
 * Правило владельца от 01.08.2026: переходы оформляются последовательными
 * датами, `assigned_to` / `end_date` — INCLUSIVE (последний день). Значит
 * «дата снятия» и «последний день работы» отличаются ровно на сутки, и UI
 * обязан показывать оператору, что именно запишется.
 *
 * Почему отдельный модуль, а не копия в каждом экране: в контуре ТУ уже был
 * боевой класс бага «оператор не видит, что дата вообще влияет» (миграция
 * `20260807120000`, семь назначений закрылись не той датой). В контуре
 * педагогики та же модалка показывала пояснение, привязанное к серверной
 * подсказке, а не к введённой дате: оператор менял дату — текст не менялся ни
 * на символ, то есть подсказка активно подтверждала неверную дату
 * (AUDIT_PEDAGOGY_UX_2026-08-21.md, «Fix now» п. 13). Две копии арифметики
 * разъезжаются молча, поэтому она здесь одна.
 *
 * Источник истины остаётся серверным (`unassign_territorial_manager`:
 * `assigned_to = p_unassigned_at - 1`), это только зеркало для показа.
 */

function shiftDays(iso: string, days: number): string {
  const date = new Date(iso + 'T00:00:00Z')
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

/** Дата снятия → последний день, когда назначение ещё действует. */
export function lastDayBefore(iso: string): string {
  return shiftDays(iso, -1)
}

/** Последний день работы (`assigned_to`) → дата снятия. Обратная к `lastDayBefore`. */
export function dayAfter(iso: string): string {
  return shiftDays(iso, 1)
}

/**
 * Что произойдёт при снятии с указанной даты.
 *
 * `removed` — интервал вырождается в пустой, поэтому строка назначения
 * удаляется целиком: закрытого периода с `assigned_to < assigned_from` не
 * бывает (CHECK `tma_period_valid`). Это не «закрытие задним числом», а
 * потеря записи, и спрашивать подтверждение нужно именно здесь.
 */
export function describeAssignmentClose(
  unassignDate: string,
  assignedFrom: string | null | undefined
): { removed: boolean; lastActiveDay: string | null } {
  if (!unassignDate) return { removed: false, lastActiveDay: null }
  if (assignedFrom && unassignDate <= assignedFrom) return { removed: true, lastActiveDay: null }
  return { removed: false, lastActiveDay: lastDayBefore(unassignDate) }
}

/**
 * Подпись кнопки снятия: будущая дата — план, сегодняшняя — снятие сейчас,
 * прошедшая — задним числом. Без неё кнопка выглядит одинаково при любой дате
 * и не подтверждает, что дата вообще учтена.
 */
export function assignmentCloseActionLabel(unassignDate: string, todayIso: string): string {
  if (!unassignDate) return 'Завершить связь'
  if (unassignDate > todayIso) return 'Запланировать'
  if (unassignDate === todayIso) return 'Завершить сегодня'
  return 'Завершить задним числом'
}
